import { z } from "zod";

/** Ordered presentation rows. Only catalog artists carry an authorization identity. */
const lineupRowId = z
  .string()
  .describe(
    "The row's id: a saved row's id to keep it, or any id unique in this lineup for a new act.",
  );

export const accessLineupRowSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("artist").describe('"artist": an artist on Lissen.'),
    id: lineupRowId,
    position: z
      .number()
      .int()
      .positive()
      .describe("Billing position: 1 is the headliner, then 2, 3 and so on."),
    artistId: z.string().min(1).describe("The Lissen artist's id."),
    collaborator: z
      .boolean()
      .optional()
      .describe("Whether this artist's managers may manage the event too."),
    name: z.string().describe("The artist's name as listed."),
    imageUrl: z
      .string()
      .max(2048)
      .nullable()
      .describe("The artist's picture URL; null for none."),
  }),
  z.object({
    type: z
      .literal("custom")
      .describe('"custom": an act that is not an artist on Lissen.'),
    id: lineupRowId,
    position: z
      .number()
      .int()
      .min(2)
      .describe("Billing position, 2 or later: a custom act never headlines."),
    artistId: z.null().describe("Always null for a custom act."),
    name: z.string().trim().min(1).max(200).describe("The act's name."),
    imageUrl: z
      .string()
      .max(2048)
      .nullable()
      .describe(
        "The act's picture, an https URL on Lissen's media CDN; null for none.",
      ),
  }),
]);

/**
 * The line-up as a fan page shows it: the same rows, but the authoring
 * invariant is gone. A partner organiser is dropped before display, so the
 * first row may be any act — even a custom one — and positions are simply
 * unique and in order.
 */
export const accessLineupDisplaySchema = z
  .array(
    z.discriminatedUnion("type", [
      accessLineupRowSchema.options[0],
      accessLineupRowSchema.options[1].extend({
        position: z.number().int().positive(),
      }),
    ]),
  )
  .min(1)
  .refine(
    (rows) => new Set(rows.map((row) => row.position)).size === rows.length,
    "A displayed lineup needs unique positions.",
  );

export const accessLineupSchema = z
  .array(accessLineupRowSchema)
  .min(1)
  .refine(
    (rows) =>
      rows.filter((row) => row.position === 1 && row.type === "artist")
        .length === 1 &&
      new Set(rows.map((row) => row.position)).size === rows.length,
    "A lineup requires one catalog owner and unique positions.",
  );

export type AccessLineupRow = z.infer<typeof accessLineupRowSchema>;

/** Preserve the catalog-only shape used by older clients. */
export function lineupArtists(lineup: readonly AccessLineupRow[]) {
  return lineup.flatMap((row) =>
    row.type === "artist"
      ? [{ id: row.artistId, name: row.name, image: row.imageUrl }]
      : [],
  );
}

/** Hydrate catalog identities from the caller's authorized catalog read. */
export function buildAccessLineup(
  rows: readonly {
    id: string;
    slotPosition: number;
    collaborator?: boolean;
    artistId: string | null;
    name: string | null;
    imageUrl: string | null;
  }[],
  artists: readonly { id: string; name: string; image: string | null }[],
): AccessLineupRow[] {
  const byId = new Map(artists.map((artist) => [artist.id, artist]));
  const owner = rows.find((row) => row.slotPosition === 1);
  if (!owner?.artistId || !byId.has(owner.artistId)) return [];

  return [...rows]
    .sort((a, b) => a.slotPosition - b.slotPosition)
    .flatMap((row): AccessLineupRow[] => {
      if (row.artistId !== null) {
        const artist = byId.get(row.artistId);
        return artist
          ? [
              {
                type: "artist",
                id: row.id,
                position: row.slotPosition,
                artistId: artist.id,
                collaborator: row.collaborator ?? false,
                name: artist.name,
                imageUrl: artist.image,
              },
            ]
          : [];
      }
      return row.name === null
        ? []
        : [
            {
              type: "custom",
              id: row.id,
              position: row.slotPosition,
              artistId: null,
              name: row.name,
              imageUrl: row.imageUrl,
            },
          ];
    })
    .map((row, index) => ({ ...row, position: index + 1 }));
}
